import { Stack, TextField, Typography } from "@mui/material";
import { type Headcount } from "@prisma/client";
import { useMemo } from "react";
import { type Input, mapMarketDataInput } from "~/components/market-data/MarketDataBrowser";
import { CompanySizeSelector } from "~/components/ui/CompanySizeSelector";
import { Modal, type ModalAction } from "~/components/ui/core/Modal";
import { OptionsFilter } from "~/components/ui/core/OptionsFilter";
import { LocationSelector } from "~/components/ui/LocationSelector";
import { useForm } from "~/hooks/useForm";
import { useListJobsQuery } from "~/hooks/useQueries";
import { useI18n } from "~/lib/i18n/useI18n";
import { compact } from "~/lib/lodash";
import { useCreatePresetMutation } from "~/pages/api/filters-preset/create-preset";
import { mapInputToFiltersPreset } from "~/services/market-data/market-data-filters/utils";

type ModalProps = Pick<React.ComponentProps<typeof Modal>, "isOpen" | "onClose"> & {
  input: Input;
};

export const SaveMarketDataFiltersModal: React.FC<ModalProps> = ({ isOpen, onClose, input }) => {
  const { t } = useI18n();
  const createPresetMutation = useCreatePresetMutation();
  const { market: formattedMarket } = mapMarketDataInput(input);

  const { levelOptions, labelOptions, industryOptions, sizeOptions, headcountOptions, fundingRoundsOptions } =
    mapInputToFiltersPreset(input);

  const { data: jobsData } = useListJobsQuery({}, { enabled: isOpen });
  const jobsOptions = useMemo(() => {
    if (!jobsData) return [];

    return compact(input.market.jobIds.map((jobId) => jobsData.jobs.find((job) => `${job.id}` === jobId)));
  }, [jobsData]);

  const hasFundingRoundsOptions = fundingRoundsOptions && fundingRoundsOptions.length > 0;
  const hasLabelOptions = labelOptions && labelOptions.length > 0;
  const hasFilters = hasFundingRoundsOptions || hasLabelOptions || industryOptions || headcountOptions || sizeOptions;

  const form = useForm({
    initialValues: {
      name: "",
      levels: input.levels,
      locations: input.market.locations,
      mergeAdvancedLevels: input.mergeAdvancedLevels,
      market: formattedMarket,
      percentiles: input.percentiles,
    },
    onSubmit: async (values) => {
      await createPresetMutation.mutateAsync({
        name: values.name,
        levels: values.levels,
        mergeAdvancedLevels: values.mergeAdvancedLevels,
        market: values.market,
        percentiles: values.percentiles,
      });
    },
  });

  const primaryAction = {
    label: t("components.market-data-filters.save-filters-modal.primary-action"),
    onClick: async () => {
      await form.submitForm();
      form.resetForm();
      onClose();
    },
    disabled: !form.values.name || createPresetMutation.isLoading,
  } satisfies ModalAction;

  const secondaryAction = {
    label: t("components.market-data-filters.save-filters-modal.secondary-action"),
    onClick: () => {
      form.resetForm();
      onClose();
    },
  } satisfies ModalAction;

  return (
    <Modal
      keepMounted={false}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      isOpen={isOpen}
      title={t("components.market-data-filters.save-filters-modal.modal-title")}
      subtitle={t("components.market-data-filters.save-filters-modal.modal-subtitle")}
      onClose={onClose}
      showCloseButton
      className="w-[800px]"
    >
      <Stack spacing={12} padding={4}>
        <Stack spacing={2}>
          <Typography variant="h3">
            {t("components.market-data-filters.save-filters-modal.save-your-filters")}
          </Typography>
          <Typography>{t("components.market-data-filters.save-filters-modal.hints")}</Typography>
        </Stack>
        <Stack spacing={4}>
          <TextField
            className="col-span-1"
            required
            label="Name your preset"
            value={form.values.name}
            onChange={(event) => form.setValues({ ...form.values, name: event.target.value })}
          />
          <Typography variant="h4">{t("components.market-data-filters.save-filters-modal.standard-fields")}</Typography>
          <Stack direction="row" spacing={2}>
            {jobsOptions && (
              <OptionsFilter
                onDelete={() => {
                  void form.setValues({ ...form.values, market: { ...form.values.market, jobsIds: [] } });
                }}
                value={jobsOptions.filter((job) => form.values.market.jobsIds.includes(`${job.id}`))}
                options={jobsOptions}
                label="Jobs"
                onChange={(event) => {
                  const jobsIds = event.map((job) => job.id);
                  void form.setValues({
                    ...form.values,
                    market: { ...form.values.market, jobsIds: jobsIds.map((jobId) => `${jobId}`) },
                  });
                }}
              />
            )}

            <LocationSelector
              multiple
              dense={false}
              value={form.values.locations}
              onChange={(event) => {
                const locationsIds = event.target.value.map((location) => location.id);
                void form.setValues({
                  ...form.values,
                  locations: event.target.value,
                  market: { ...form.values.market, locationsIds },
                });
              }}
            />

            {levelOptions && (
              <OptionsFilter
                value={levelOptions.filter((level) => form.values.levels.includes(level.id))}
                options={levelOptions}
                label="Levels"
                onDelete={(event) => {
                  const levels = event.map((level) => level.id);
                  void form.setValues({ ...form.values, levels });
                }}
                onChange={(event) => {
                  const levels = event.map((level) => level.id);
                  void form.setValues({ ...form.values, levels });
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stack spacing={2}>
          {hasFilters && (
            <Typography variant="h4">
              {t("components.market-data-filters.save-filters-modal.filters-information")}
            </Typography>
          )}

          <Stack direction="row" spacing={2} paddingBottom={8}>
            {headcountOptions && (
              <OptionsFilter
                value={[headcountOptions].filter((headcount) => form.values.market.headcount?.includes(headcount.id))}
                options={[headcountOptions]}
                label="Headcount"
                onChange={(event) => {
                  if (event[0]?.id === null) {
                    void form.setValues({ ...form.values, market: { ...form.values.market, headcount: null } });
                  }

                  void form.setValues({
                    ...form.values,
                    market: { ...form.values.market, headcount: event[0]?.id as Headcount },
                  });
                }}
              />
            )}

            {hasFundingRoundsOptions && (
              <OptionsFilter
                value={fundingRoundsOptions.filter(
                  (fundingRound) => form.values.market.fundingRounds?.includes(fundingRound.id)
                )}
                options={fundingRoundsOptions}
                label="Funding Rounds"
                onChange={(event) => {
                  void form.setValues({
                    ...form.values,
                    market: { ...form.values.market, fundingRounds: event.map((fundingRound) => fundingRound.id) },
                  });
                }}
              />
            )}

            {/* Label */}
            {hasLabelOptions && (
              <OptionsFilter
                value={labelOptions.filter((label) => form.values.market.labelsIds?.includes(label.id))}
                options={labelOptions}
                label="Label"
                onChange={(event) => {
                  void form.setValues({
                    ...form.values,
                    market: {
                      ...form.values.market,
                      labelsIds: event[0]?.id ? [event[0]?.id] : [],
                    },
                  });
                }}
              />
            )}
            {/* Industry */}
            {industryOptions && (
              <OptionsFilter
                value={industryOptions.filter((industry) =>
                  [...form.values.market.industriesIds, form.values.market.industryId].includes(industry.id)
                )}
                options={industryOptions}
                label="Industries"
                onChange={(event) => {
                  if (event === null) {
                    void form.setValues({
                      ...form.values,
                      market: { ...form.values.market, industryId: null, industriesIds: [] },
                    });
                  }

                  void form.setValues({
                    ...form.values,
                    market: {
                      ...form.values.market,
                      industryId: event[0]?.id,
                      industriesIds: event.map((industry) => industry.id),
                    },
                  });
                }}
              />
            )}
          </Stack>

          {sizeOptions && (
            <Stack className="w-1/4">
              <Typography variant="subtitle2"> Headcount </Typography>
              <CompanySizeSelector
                direction="column"
                value={{
                  min: form.values.market.size.min ?? 0,
                  max: form.values.market.size.max ?? 0,
                }}
                onChange={(event) => {
                  void form.setValues({
                    ...form.values,
                    market: { ...form.values.market, size: event.target.value },
                  });
                }}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};
